/** @jsx jsx */
import { css, jsx } from '@emotion/core';

import IntegrationSidebar from 'app/developer-v3/components/IntegrationSidebar';
import { ReviewStatus } from 'app/developer-v3/types/statuses';
import { getAppUrl, getAppVersionUrl, getStatus } from 'app/developer-v3/utils';
import {
  CurrentVersionReadOnlyReason,
  useCurrentVersionReadOnlyReason,
  usePrivatePlatform,
} from 'app/developer-v3/hooks';

import SectionBuild from './SectionBuild';
import SectionDashboard from './SectionDashboard';
import SectionEmbed from './SectionEmbed';
import SectionInsights from './SectionInsights';
import SectionManage from './SectionManage';
import Summary from './Summary';
import useSidebarQuery from './useSidebarQuery';

import type { AppSummary as AppSummaryType } from 'app/developer-v3/types';
import { EmbedToast } from './EmbedToast';

const Styles = {
  sectionsWrapper: css`
    height: 100%;
    overflow-y: auto;
  `,
};

type Props = {
  appSummary: AppSummaryType;
  currentUrl: string;
  isPublicIntention: boolean;
  onVersionChange: (versionNumber: string) => {};
};

function Sidebar(props: Props) {
  const { appSummary, currentUrl, isPublicIntention, onVersionChange } = props;

  const {
    appId,
    definition,
    definitionOverride,
    imageUrl,
    isGUIIntegration,
    isPending,
    isPrivate,
    isPublished,
    status,
    title,
    version,
    versionLifecycle,
    versions,
    users = {},
  } = appSummary;
  const baseUrl = getAppVersionUrl(appId, version);
  const versionlessUrl = getAppUrl(appId);

  const { exceedLimit } = users;

  const { isPublicIntentionElementVisible } = usePrivatePlatform();

  const { hasIssues, issuesCount, reviewStatus } = useSidebarQuery(appId);

  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const isLifecyclePending =
    currentVersionReadOnlyReason ===
    CurrentVersionReadOnlyReason.PENDING_VERSION_LIFECYCLE;

  return (
    <IntegrationSidebar
      items={
        appId && (
          <div css={Styles.sectionsWrapper}>
            {(isPublished || isPending) && (
              <SectionDashboard
                currentUrl={currentUrl}
                versionlessUrl={versionlessUrl}
              />
            )}
            <SectionInsights
              currentUrl={currentUrl}
              versionlessUrl={versionlessUrl}
            />
            <SectionBuild
              baseUrl={baseUrl}
              currentUrl={currentUrl}
              definition={definition}
              definitionOverride={definitionOverride}
              isGUIIntegration={isGUIIntegration}
              onVersionChange={onVersionChange}
              usersExceedLimit={exceedLimit}
              version={version}
              versionLifecycle={versionLifecycle}
              versions={versions}
            />
            <SectionManage
              appId={appId}
              baseUrl={baseUrl}
              currentUrl={currentUrl}
              hasIssues={hasIssues}
              isLifecyclePending={isLifecyclePending}
              isPrivate={isPrivate}
              isPublicIntention={isPublicIntention}
              isPublished={isPublished}
              issuesCount={issuesCount}
              reviewStatus={reviewStatus}
              versionlessUrl={versionlessUrl}
            />
            {isPublicIntentionElementVisible && (
              <SectionEmbed
                appSummary={appSummary}
                currentUrl={currentUrl}
                versionlessUrl={versionlessUrl}
              />
            )}
            {isPublicIntentionElementVisible && <EmbedToast appId={appId} />}
          </div>
        )
      }
      summary={
        <Summary
          appId={appId}
          canPublish={
            (isPrivate && isPublicIntention) ||
            reviewStatus === ReviewStatus.pushedBack
          }
          imageUrl={imageUrl}
          status={getStatus(status)}
          title={title}
          version={version}
        />
      }
    />
  );
}

export default Sidebar;
